/* eslint-disable react/jsx-no-target-blank */
import React, {useState, useEffect} from 'react'
import {SidebarMenuItem} from './SidebarMenuItem'
import {SidebarMenuItemWithSub} from './SidebarMenuItemWithSub'
import './SidebarMenuMain.scss'
import {useIntl} from 'react-intl'
import {Logout, AuthPage, useAuth} from '../../../../../app/modules/auth'
import axios from 'axios'
import {getAdminToken} from '../../../../../app/modules/admin/auth/_requests'
import {ReferralPopup} from '../../../../../app/modules/referrals/ReferralPopup'
import {ReferralPromoPopup} from '../../../../../app/modules/referrals/ReferralPromoPopup'

const API_URL = process.env.REACT_APP_API_URL || ''

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

// Customer-service WhatsApp, the same number the onboarding wizard and the in-app
// tutorial use. 0722210190 → international (drop the leading 0, prepend 972).
const SUPPORT_WA = '972722210190'

const SidebarMenuMain = () => {
  const intl = useIntl()
  const {currentUser, getBot} = useAuth()
  const supportWaLink = `https://wa.me/${SUPPORT_WA}?text=${encodeURIComponent(
    `שלום, אני צריך/ה עזרה 🙏\nשם משתמש: ${(currentUser as any)?.username || 'לקוח'}`
  )}`
  const [isAdmin, setIsAdmin] = useState(false)
  const [groupSchedulingEnabled, setGroupSchedulingEnabled] = useState(false)
  const [th3postEnabled, setTh3postEnabled] = useState(false)
  const [bulkProfilesEnabled, setBulkProfilesEnabled] = useState(false)
  const [hasRecentComments, setHasRecentComments] = useState(false)
  const [scheduleEnabled, setScheduleEnabled] = useState(false)
  const [referralSidebarEnabled, setReferralSidebarEnabled] = useState(false)
  const [hasVirtualNumber, setHasVirtualNumber] = useState(false)
  const [showReferralPopup, setShowReferralPopup] = useState(false)
  const [installPrompt, setInstallPrompt] = useState<BeforeInstallPromptEvent | null>(null)
  const [isInstalled, setIsInstalled] = useState(false)

  useEffect(() => {
    const checkAdmin = async () => {
      try {
        const adminToken = getAdminToken()
        const headers: Record<string, string> = {}
        if (adminToken) {
          headers['Authorization'] = `Bearer ${adminToken}`
        }
        const response = await axios.get(`${API_URL}/admin/check-auth`, {
          headers,
          withCredentials: true
        })
        setIsAdmin(response.data.loggedIn === true)
      } catch {
        setIsAdmin(false)
      }
    }
    checkAdmin()
  }, [])

  useEffect(() => {
    const checkMarketplace = async () => {
      try {
        // Get the JWT token from localStorage
        const authToken = localStorage.getItem('kt-auth-react-v')
        if (!authToken) return

        const parsed = JSON.parse(authToken)
        if (!parsed?.api_token) return

        const response = await axios.get(`${API_URL}/api/account-settings`, {
          headers: {
            Authorization: `Bearer ${parsed.api_token}`
          }
        })
        setGroupSchedulingEnabled(response.data.group_scheduling_enabled === true)
        setTh3postEnabled(response.data.th3post_enabled === true)
        setBulkProfilesEnabled(response.data.bulk_profiles_enabled === true)
        setHasRecentComments(response.data.has_recent_comments === true)
        setScheduleEnabled(response.data.schedule_enabled === true)
        setReferralSidebarEnabled(response.data.referral_sidebar_enabled === true)
      } catch {
        setGroupSchedulingEnabled(false)
        setTh3postEnabled(false)
        setHasRecentComments(false)
        setScheduleEnabled(false)
      }
    }
    checkMarketplace()
  }, [])

  // Only show the virtual-number menu item to clients who have one assigned (R6).
  useEffect(() => {
    const loadVirtualNumber = async () => {
      try {
        const authToken = localStorage.getItem('kt-auth-react-v')
        if (!authToken) return
        const parsed = JSON.parse(authToken)
        if (!parsed?.api_token) return
        const res = await axios.get(`${API_URL}/api/my-virtual-number`, {
          headers: {Authorization: `Bearer ${parsed.api_token}`},
        })
        setHasVirtualNumber(Array.isArray(res.data?.numbers) && res.data.numbers.length > 0)
      } catch {
        setHasVirtualNumber(false)
      }
    }
    loadVirtualNumber()
  }, [])

  useEffect(() => {
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches
    if (isStandalone) {
      setIsInstalled(true)
      return
    }

    const handler = (e: Event) => {
      e.preventDefault()
      setInstallPrompt(e as BeforeInstallPromptEvent)
    }

    window.addEventListener('beforeinstallprompt', handler)
    window.addEventListener('appinstalled', () => setIsInstalled(true))

    return () => {
      window.removeEventListener('beforeinstallprompt', handler)
    }
  }, [])

  const handleInstallClick = async (e: React.MouseEvent) => {
    e.preventDefault()
    if (installPrompt) {
      installPrompt.prompt()
      const { outcome } = await installPrompt.userChoice
      if (outcome === 'accepted') {
        setIsInstalled(true)
      }
      setInstallPrompt(null)
    } else {
      // Fallback for iOS/browsers without beforeinstallprompt
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent)
      if (isIOS) {
        alert('להתקנת האפליקציה:\n1. לחץ על כפתור השיתוף (⬆️) בתחתית המסך\n2. בחר "הוסף למסך הבית"')
      } else {
        alert('להתקנת האפליקציה:\nלחץ על תפריט הדפדפן (⋮) ובחר "התקן אפליקציה" או "הוסף למסך הבית"')
      }
    }
  }

  return (
    <>
      {/* Monthly referral-promo popup (self-checks eligibility; ?preview_referral_promo=1 to preview) */}
      <ReferralPromoPopup />

      {/* Referral Program Button — first in the sidebar */}
      {referralSidebarEnabled && <>
        <div className="menu-item">
          <a
            className="menu-link without-sub"
            href="#"
            onClick={(e) => { e.preventDefault(); setShowReferralPopup(true) }}
            style={{
              background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
              borderRadius: '8px',
              margin: '8px 12px',
              padding: '10px 14px',
            }}
          >
            <span className="menu-icon">
              <i className="bi bi-gift-fill" style={{ color: '#fff', fontSize: '18px' }}></i>
            </span>
            <span className="menu-title" style={{ color: '#fff', fontWeight: 600 }}>הזמן חבר וקבל חודש חינם</span>
          </a>
        </div>
        <ReferralPopup show={showReferralPopup} onClose={() => setShowReferralPopup(false)} />
      </>}

      {/* Admin: Back to user's admin page button */}
      {isAdmin && currentUser && (
        <div className="menu-item mb-3">
          <a
            href={`/admin/accounts/${(currentUser as any)._id || currentUser.id}`}
            className="menu-link without-sub"
            style={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              borderRadius: '8px',
              margin: '0 12px 8px',
              padding: '10px 14px',
            }}
          >
            <span className="menu-icon">
              <i className="bi bi-arrow-left-circle-fill" style={{ color: '#fff', fontSize: '18px' }}></i>
            </span>
            <span className="menu-title" style={{ color: '#fff', fontWeight: 600 }}>חזרה לניהול</span>
          </a>
        </div>
      )}

      {/*<SidebarMenuItem
        to='/inbox'
        icon='/media/icons/duotune/general/comments-solid.svg'
        title='Inbox'
        fontIcon='bi-layers'
      />*/}

      {/*<SidebarMenuItem
        to='/contacts'
        icon='/media/icons/duotune/general/user-group-solid.svg'
        title='Contacts'
        fontIcon='bi-layers'
      />*/}

      {/*<SidebarMenuItem
        to='/content'
        icon='/media/icons/duotune/general/message-solid.svg'
        title='Content'
        fontIcon='bi-layers'
      />*/}

      {/*<SidebarMenuItem
        to='/add-ons'
        icon='/media/icons/duotune/general/square-plus-solid.svg'
        title='Add-ons'
        fontIcon='bi-layers'
      />*/}

      {/*<SidebarMenuItem
        to='/groups'
        icon='/media/icons/duotune/general/users-rectangle-solid.svg'
        title='Groups'
        fontIcon='bi-layers'
      />*/}

      {/*<SidebarMenuItem
        to='/settings'
        icon='/media/icons/duotune/general/gear-solid.svg'
        title='Settings'
        fontIcon='bi-layers'
      />*/}
  {currentUser && <>

      {/* Welcome Message */}
      <div className="sidebar-welcome">
        שלום {currentUser?.first_name || 'משתמש'} 👋
      </div>

      <SidebarMenuItem
        to='/campaigns'
        icon='/media/icons/duotune/communication/com005.svg'
        title={intl.formatMessage({id: 'MENU.CAMPAIGNS'})}
        fontIcon='bi-layers'
      />
      {isAdmin && <SidebarMenuItem
        to='/contacts'
        icon='/media/icons/duotune/communication/com006.svg'
        title='אנשי קשר'
        fontIcon='bi-layers'
      />}
      {bulkProfilesEnabled && (
        <SidebarMenuItem
          to='/connect-profiles'
          icon='/media/icons/duotune/communication/com005.svg'
          title={'חיבור פרופילים'}
          fontIcon='bi-layers'
        />
      )}
      {/*<SidebarMenuItem
        to='/campaigns?groups'
        icon='/media/icons/duotune/general/comments-solid.svg'
        title={intl.formatMessage({id: 'MENU.GROUPS'})}
        fontIcon='bi-layers'
      />*/}
      {/*<SidebarMenuItem
        to='/profiles'
        icon='/media/icons/duotune/general/comments-solid.svg'
        title={intl.formatMessage({id: 'MENU.CONNECT_PROFILE'})}
        fontIcon='bi-layers'
      />*/}     
      {/*<SidebarMenuItem
        to='/plans'
        icon='/media/icons/duotune/general/comments-solid.svg'
        title={intl.formatMessage({id: 'MENU.PLANS'})}
        fontIcon='bi-layers'
      />*/}
      {/*<div className="menu-item">
        <a
          className="menu-link without-sub"
          target="_blank"
          href="https://www.facebook.com/aaaaaaaaa/allactivity/?activity_history=false&category_key=GROUPPOSTS&manage_mode=false&should_load_landing_page=false"
        >
          <span className="menu-icon">
            {" "}
            <span className="svg-icon svg-icon-2">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 640 512"
                className="mh-50px"
              >
                <path d="M208 352c114.9 0 208-78.8 208-176S322.9 0 208 0S0 78.8 0 176c0 38.6 14.7 74.3 39.6 103.4c-3.5 9.4-8.7 17.7-14.2 24.7c-4.8 6.2-9.7 11-13.3 14.3c-1.8 1.6-3.3 2.9-4.3 3.7c-.5 .4-.9 .7-1.1 .8l-.2 .2 0 0 0 0C1 327.2-1.4 334.4 .8 340.9S9.1 352 16 352c21.8 0 43.8-5.6 62.1-12.5c9.2-3.5 17.8-7.4 25.3-11.4C134.1 343.3 169.8 352 208 352zM448 176c0 112.3-99.1 196.9-216.5 207C255.8 457.4 336.4 512 432 512c38.2 0 73.9-8.7 104.7-23.9c7.5 4 16 7.9 25.2 11.4c18.3 6.9 40.3 12.5 62.1 12.5c6.9 0 13.1-4.5 15.2-11.1c2.1-6.6-.2-13.8-5.8-17.9l0 0 0 0-.2-.2c-.2-.2-.6-.4-1.1-.8c-1-.8-2.5-2-4.3-3.7c-3.6-3.3-8.5-8.1-13.3-14.3c-5.5-7-10.7-15.4-14.2-24.7c24.9-29 39.6-64.7 39.6-103.4c0-92.8-84.9-168.9-192.6-175.5c.4 5.1 .6 10.3 .6 15.5z" />
              </svg>
            </span>
          </span>
          <span className="menu-title">נתונים</span>
        </a>
      </div>*/}
      {<SidebarMenuItem
        to='/stats'
        icon='/media/icons/duotune/graphs/gra001.svg'
        title={intl.formatMessage({id: 'MENU.STATS'})}
        fontIcon='bi-layers'
      />}
      {/* Customer API (keys + docs) — only for customers the admin turned it on for. */}
      {!!(currentUser as any)?.api_access_enabled && (
        <SidebarMenuItem
          to='/api-access'
          icon='/media/icons/duotune/coding/cod003.svg'
          title='API'
          fontIcon='bi-code-slash'
        />
      )}
      {hasVirtualNumber && (
        <SidebarMenuItem
          to='/virtual-number'
          icon='/media/icons/duotune/communication/com005.svg'
          title='מספר וירטואלי'
          fontIcon='bi-telephone-forward'
        />
      )}
      {/*<SidebarMenuItem
        to='/update-payment-information'
        icon='/media/icons/duotune/general/comments-solid.svg'
        title={intl.formatMessage({id: 'MENU.UPDATE_PAYMENT'})}
        fontIcon='bi-layers'
      />*/}
      <SidebarMenuItem
        to='/invoices'
        icon='/media/icons/duotune/general/gen049.svg'
        title="הפרופיל שלי"
        fontIcon='bi-layers'
      />
      <SidebarMenuItem
        to='/invoices?tab=invoices'
        icon='/media/icons/duotune/finance/fin002.svg'
        title="חשבוניות"
        fontIcon='bi-layers'
      />
      {groupSchedulingEnabled && <SidebarMenuItem
        to='/group-scheduling'
        icon='/media/icons/duotune/general/gen013.svg'
        title="תזמון קבוצות"
        fontIcon='bi-clock'
      />}
      <SidebarMenuItem
        to='/marketplace'
        icon='/media/icons/duotune/ecommerce/ecm001.svg'
        title="מרקטפלייס"
        fontIcon='bi-shop'
      />
      {(isAdmin || hasRecentComments) && <SidebarMenuItem
        to='/facebook-comments'
        icon='/media/icons/duotune/communication/com012.svg'
        title="תגובות פייסבוק"
        fontIcon='bi-chat'
      />}
      {(isAdmin || scheduleEnabled) && <SidebarMenuItem
        to='/schedule'
        icon='/media/icons/duotune/general/gen014.svg'
        title="לוח זמנים"
        fontIcon='bi-calendar'
        {...(isAdmin && !scheduleEnabled ? { badge: "אדמין", badgeClass: "badge-light-danger" } : {})}
      />}
      {(isAdmin || th3postEnabled) && <SidebarMenuItem
        to='/th3post-sync'
        icon='/media/icons/duotune/arrows/arr029.svg'
        title="ייבוא מ-Th3Post"
        fontIcon='bi-arrow-repeat'
        badge="חדש"
      />}
      <SidebarMenuItem
        to='/support'
        icon='/media/icons/duotune/communication/com007.svg'
        title="תמיכה"
        fontIcon='bi-layers'
      />

      <SidebarMenuItem
        to='campaigns?tut=full'
        title={'המדריך המלא'}
        icon='/media/icons/duotune/general/gen025.svg'
        fontIcon='bi-layers'
      />

      <SidebarMenuItem
        to='/tutorial-demo'
        title={'סרטון הדרכה מקיף'}
        icon='/media/icons/duotune/general/gen006.svg'
        fontIcon='bi-play-circle'
      />

      {/* Install App Button - only show when native install prompt is available or on mobile */}
      {!isInstalled && (installPrompt || /Android|iPhone|iPad|iPod/i.test(navigator.userAgent)) && (
        <div className="menu-item">
          <a
            className="menu-link without-sub"
            href="#"
            onClick={handleInstallClick}
            style={{
              background: 'linear-gradient(135deg, #3699ff 0%, #1b84ff 100%)',
              borderRadius: '8px',
              margin: '8px 12px',
              padding: '10px 14px',
            }}
          >
            <span className="menu-icon">
              <i className="bi bi-download" style={{ color: '#fff', fontSize: '18px' }}></i>
            </span>
            <span className="menu-title" style={{ color: '#fff', fontWeight: 600 }}>התקן אפליקציה</span>
          </a>
        </div>
      )}

      {/* Customer service on WhatsApp. The support widget floats over the page and is
          easy to miss; this is the same escape hatch in the place people look for
          navigation. Prefilled with the username so support knows who it is. */}
      <div className="menu-item">
        <a
          className="menu-link without-sub"
          href={supportWaLink}
          target="_blank"
          rel="noreferrer"
          style={{
            background: 'linear-gradient(135deg, #25d366 0%, #1fb855 100%)',
            borderRadius: '8px',
            margin: '8px 12px',
            padding: '10px 14px',
          }}
        >
          <span className="menu-icon">
            <i className="bi bi-whatsapp" style={{ color: '#fff', fontSize: '18px' }}></i>
          </span>
          <span className="menu-title" style={{ color: '#fff', fontWeight: 600 }}>תמיכה בוואטסאפ</span>
        </a>
      </div>

</>}
{currentUser && currentUser.is_affiliate && <SidebarMenuItem
        to='/affiliate/dashboard'
        title={intl.formatMessage({id: 'MENU.AFFILIATE'})}
        icon='/media/icons/duotune/general/gen025.svg'
        fontIcon='bi-layers'
      />}











      {/*<SidebarMenuItem
        to='/invoices'
        icon='/media/icons/duotune/general/comments-solid.svg'
        title={intl.formatMessage({id: 'MENU.INVOICES'})}
        fontIcon='bi-layers'
      />*/}
      {/*<SidebarMenuItem
        to='/scheduling-distribution'
        icon='/media/icons/duotune/maps/map007.svg'
        title={intl.formatMessage({id: 'MENU.SCHEDULING_DISTRIBUTION'})}
        fontIcon='bi-layers'
      />*/}
    </>
  )
}

export {SidebarMenuMain}
